import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useExternalLink } from './ExternalLinkWarning';
import SearchBar from './SearchBar';
import './Navbar.css';

const Navbar = () => {
  const { t, translations } = useLanguage();
  const { showWarning } = useExternalLink();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isStickyTop, setIsStickyTop] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isResourcesDropdownOpen, setIsResourcesDropdownOpen] = useState(false);
  const [isTendersNoticesDropdownOpen, setIsTendersNoticesDropdownOpen] = useState(false);
  const [openNestedDropdown, setOpenNestedDropdown] = useState(null);
  const location = useLocation();

  // Handle scroll effect for navbar
  useEffect(() => {
    let ticking = false;
    
    const getScrollThreshold = () => {
      const width = window.innerWidth;
      if (width <= 480) return 80; // Small mobile
      if (width <= 768) return 90; // Mobile
      if (width <= 1024) return 95; // Tablet
      return 100; // Desktop
    };
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const threshold = getScrollThreshold();
          setIsScrolled(scrollY > 50);
          // Make navbar sticky when it reaches the accessibility bar
          setIsStickyTop(scrollY > threshold);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close dropdown when clicking outside (only for mobile or when needed)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close on click outside for mobile devices or when dropdown is open
      if (isAboutDropdownOpen && !event.target.closest('.nav-dropdown-container') && window.innerWidth <= 768) {
        setIsAboutDropdownOpen(false);
      }
      if (isServicesDropdownOpen && !event.target.closest('.nav-dropdown-container') && window.innerWidth <= 768) {
        setIsServicesDropdownOpen(false);
      }
      if (isResourcesDropdownOpen && !event.target.closest('.nav-dropdown-container') && window.innerWidth <= 768) {
        setIsResourcesDropdownOpen(false);
      }
      if (isTendersNoticesDropdownOpen && !event.target.closest('.nav-dropdown-container') && window.innerWidth <= 768) {
        setIsTendersNoticesDropdownOpen(false);
      }
      if (openNestedDropdown && !event.target.closest('.nested-dropdown-container') && window.innerWidth <= 768) {
        setOpenNestedDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAboutDropdownOpen, isServicesDropdownOpen, isResourcesDropdownOpen, isTendersNoticesDropdownOpen, openNestedDropdown]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle about dropdown hover
  const handleAboutDropdownEnter = () => {
    setIsAboutDropdownOpen(true);
  };

  const handleAboutDropdownLeave = () => {
    setIsAboutDropdownOpen(false);
  };

  // Handle services dropdown hover
  const handleServicesDropdownEnter = () => {
    setIsServicesDropdownOpen(true);
  };

  const handleServicesDropdownLeave = () => {
    setIsServicesDropdownOpen(false);
  };

  // Handle resources dropdown hover
  const handleResourcesDropdownEnter = () => {
    setIsResourcesDropdownOpen(true);
  };

  const handleResourcesDropdownLeave = () => {
    setIsResourcesDropdownOpen(false);
  };

  // Handle tenders notices dropdown hover
  const handleTendersNoticesDropdownEnter = () => {
    setIsTendersNoticesDropdownOpen(true);
  };

  const handleTendersNoticesDropdownLeave = () => {
    setIsTendersNoticesDropdownOpen(false);
  };

  // Handle external link clicks
  const handleExternalLink = (e, url) => {
    e.preventDefault();
    showWarning(url);
  };

  // Navigation items
  const navItems = [
    { path: '/', label: t('home') },
    { 
      path: null, 
      label: t('about'),
      hasDropdown: true,
      dropdownItems: [
        { path: '/overview', label: translations.aboutDropdown?.overview || 'आढावा' },
        { path: '/administrative-structure', label: translations.aboutDropdown?.administrativeStructure || 'प्रशासकिय रचना' },
        { path: '/board-project', label: translations.aboutDropdown?.boardProject || 'मंडळ प्रकल्प' },
        { path: '/board-members', label: translations.aboutDropdown?.boardMembers || 'मंडळ सदस्य' },
        { path: '/annual-report', label: translations.aboutDropdown?.annualReport || 'वार्षिक गोषवारा' },
        { path: '/circulars', label: translations.aboutDropdown?.circulars || 'कर्मचाऱ्यांसाठी परिपत्रके' },
        { 
          path: null, 
          label: t('mediaGallery'),
          hasDropdown: true,
          dropdownItems: [
            { path: '/photo-gallery', label: t('photoGallery') },
            { path: '/video-gallery', label: t('videoGallery') }
          ]
        }
      ]
    },
    { path: '/workers-welfare', label: t('workersWelfare') },
    { 
      path: null, 
      label: t('resources'),
      hasDropdown: true,
      dropdownItems: [
        { path: '/right-to-info', label: t('rightToInfo') },
        { path: '/right-to-service', label: t('rightToService') }
      ]
    },
    { 
      path: null, 
      label: t('tendersNotices'),
      hasDropdown: true,
      dropdownItems: [
        { path: '/tenders-notices', label: t('eTender') },
        { path: 'https://mahatenders.gov.in/nicgep/app', label: t('mahaTender'), isExternal: true },
        { path: 'https://gem.gov.in/', label: t('gemPortal'), isExternal: true }
      ]
    },
    { 
      path: null, 
      label: t('services'),
      hasDropdown: true,
      dropdownItems: [
        { path: '/list-of-services', label: translations.servicesDropdown?.listOfServices || 'सेवांची यादी' },
        { path: 'https://public.mlwb.in/public/UniversalApply', label: translations.servicesDropdown?.applyHere || 'अर्ज करा', isExternal: true },
        { path: '/feedback', label: translations.servicesDropdown?.feedback || 'अभिप्राय' },
        { path: '/grievance', label: translations.servicesDropdown?.grievance || 'तक्रार नोंदवा' },
        { path: '/faq', label: translations.servicesDropdown?.faq || 'वारंवार विचारले जाणारे प्रश्न' },
        { path: '/dashboard', label: translations.servicesDropdown?.dashboard || 'डॅशबोर्ड' },
        { path: 'https://public.mlwb.in/public', label: translations.servicesDropdown?.publicPortal || 'पब्लिक पोर्टल', isExternal: true },
        { path: 'https://aaplesarkar.mahaonline.gov.in/', label: translations.servicesDropdown?.aapleSarkar || 'आपले सरकार', isExternal: true }
      ]
    },
    { path: '/100-days-program', label: t('hundredDaysProgram') },
    { path: '/contact', label: t('contact') }
  ];

  return (
    <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''} ${isStickyTop ? 'sticky-top' : ''}`} data-read-section="navbar">
      <div className="navbar-container">
        {/* Left Section - Empty for balance */}
        <div className="navbar-left">
          {/* Empty space for visual balance */}
        </div>

        {/* Center Section - Desktop Navigation */}
        <div className="navbar-center">
          <div className="navbar-nav desktop-nav">
            {navItems.map((item) => (
              <div key={item.path || item.label} className="nav-item-wrapper">
                {item.hasDropdown ? (
                  <div 
                    className="nav-dropdown-container"
                    onMouseEnter={
                      item.label === t('about') ? handleAboutDropdownEnter :
                      item.label === t('services') ? handleServicesDropdownEnter :
                      item.label === t('resources') ? handleResourcesDropdownEnter :
                      item.label === t('tendersNotices') ? handleTendersNoticesDropdownEnter :
                      null
                    }
                    onMouseLeave={
                      item.label === t('about') ? handleAboutDropdownLeave :
                      item.label === t('services') ? handleServicesDropdownLeave :
                      item.label === t('resources') ? handleResourcesDropdownLeave :
                      item.label === t('tendersNotices') ? handleTendersNoticesDropdownLeave :
                      null
                    }
                  >
                    <span
                      className="nav-link nav-link-dropdown"
                      style={{ cursor: 'default' }}
                    >
                      {item.label}
                      <svg 
                        width="12" 
                        height="12" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                        className={`dropdown-arrow ${
                          (item.label === t('about') ? isAboutDropdownOpen :
                           item.label === t('services') ? isServicesDropdownOpen :
                           item.label === t('resources') ? isResourcesDropdownOpen :
                           item.label === t('tendersNotices') ? isTendersNoticesDropdownOpen : false) ? 'dropdown-arrow-open' : ''
                        }`}
                      >
                        <polyline points="6,9 12,15 18,9"></polyline>
                      </svg>
                    </span>
                    <div 
                      className={`nav-dropdown ${
                        (item.label === t('about') ? isAboutDropdownOpen :
                         item.label === t('services') ? isServicesDropdownOpen :
                         item.label === t('resources') ? isResourcesDropdownOpen :
                         item.label === t('tendersNotices') ? isTendersNoticesDropdownOpen : false) ? 'nav-dropdown-open' : ''
                      }`}
                    >
                      {item.dropdownItems.map((dropdownItem) => {
                        // Check if this dropdown item has nested dropdown
                        if (dropdownItem.hasDropdown) {
                          return (
                            <div
                              key={dropdownItem.label}
                              className="nested-dropdown-container"
                              onMouseEnter={() => setOpenNestedDropdown(dropdownItem.label)}
                              onMouseLeave={() => setOpenNestedDropdown(null)}
                            >
                              <span className="nav-dropdown-link nav-dropdown-link-nested">
                                {dropdownItem.label}
                                <svg 
                                  width="10" 
                                  height="10" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                  className="nested-dropdown-arrow"
                                >
                                  <polyline points="9,18 15,12 9,6"></polyline>
                                </svg>
                              </span>
                              {openNestedDropdown === dropdownItem.label && (
                                <div className="nested-dropdown">
                                  {dropdownItem.dropdownItems.map((nestedItem) => (
                                    nestedItem.isExternal ? (
                                      <a
                                        key={nestedItem.path}
                                        href={nestedItem.path}
                                        className="nav-dropdown-link"
                                        onClick={(e) => {
                                          handleExternalLink(e, nestedItem.path);
                                          setOpenNestedDropdown(null);
                                          if (item.label === t('about')) handleAboutDropdownLeave();
                                          if (item.label === t('services')) handleServicesDropdownLeave();
                                        }}
                                      >
                                        {nestedItem.label}
                                      </a>
                                    ) : (
                                      <Link
                                        key={nestedItem.path}
                                        to={nestedItem.path}
                                        className="nav-dropdown-link"
                                        onClick={() => {
                                          setOpenNestedDropdown(null);
                                          if (item.label === t('about')) handleAboutDropdownLeave();
                                          if (item.label === t('services')) handleServicesDropdownLeave();
                                        }}
                                      >
                                        {nestedItem.label}
                                      </Link>
                                    )
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }
                        
                        // Regular dropdown item (link)
                        return dropdownItem.isExternal ? (
                          <a
                            key={dropdownItem.path}
                            href={dropdownItem.path}
                            className="nav-dropdown-link"
                            onClick={(e) => {
                              handleExternalLink(e, dropdownItem.path);
                              if (item.label === t('about')) handleAboutDropdownLeave();
                              if (item.label === t('services')) handleServicesDropdownLeave();
                              if (item.label === t('resources')) handleResourcesDropdownLeave();
                              if (item.label === t('tendersNotices')) handleTendersNoticesDropdownLeave();
                            }}
                          >
                            {dropdownItem.label}
                          </a>
                        ) : (
                          <Link
                            key={dropdownItem.path}
                            to={dropdownItem.path}
                            className="nav-dropdown-link"
                            onClick={
                              item.label === t('about') ? handleAboutDropdownLeave :
                              item.label === t('services') ? handleServicesDropdownLeave :
                              item.label === t('resources') ? handleResourcesDropdownLeave :
                              item.label === t('tendersNotices') ? handleTendersNoticesDropdownLeave :
                              null
                            }
                          >
                            {dropdownItem.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  >
                    {item.path === '/' && (
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9,22 9,12 15,12 15,22"/>
                      </svg>
                    )}
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Section - Mobile Controls */}
        <div className="navbar-right">
          <div className="navbar-controls">
            {/* Search Bar */}
            <SearchBar />

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={isMenuOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div className={`mobile-nav ${isMenuOpen ? 'mobile-nav-open' : ''}`}>
        {/* Mobile Nav Header with Search and Toggle */}
        <div className="mobile-nav-header">
          <div className="mobile-nav-header-content">
            {/* Search Bar */}
            <div className="mobile-nav-search">
              <SearchBar />
            </div>
            
            {/* Close Button */}
            <button
              className="mobile-nav-close"
              onClick={toggleMobileMenu}
              aria-label="Close mobile menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile Nav Content */}
        <div className="mobile-nav-content">
          {navItems.map((item) => (
            <div key={item.path || item.label}>
              {item.path ? (
                <Link
                  to={item.path}
                  className={`mobile-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.path === '/' && (
                    <svg 
                      width="18" 
                      height="18" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                      <polyline points="9,22 9,12 15,12 15,22"/>
                    </svg>
                  )}
                  {item.label}
                </Link>
              ) : (
                <span className="mobile-nav-link" style={{ cursor: 'default' }}>
                  {item.label}
                </span>
              )}
              {/* Show dropdown items as regular links on mobile */}
              {item.hasDropdown && item.dropdownItems && (
                <div className="mobile-dropdown-items">
                  {item.dropdownItems.map((dropdownItem) => {
                    // Handle nested dropdowns on mobile
                    if (dropdownItem.hasDropdown && dropdownItem.dropdownItems) {
                      return (
                        <div key={dropdownItem.label}>
                          <span className="mobile-nav-link mobile-dropdown-link">
                            {dropdownItem.label}
                          </span>
                          <div className="mobile-dropdown-items">
                            {dropdownItem.dropdownItems.map((nestedItem) => (
                              nestedItem.isExternal ? (
                                <a
                                  key={nestedItem.path}
                                  href={nestedItem.path}
                                  className="mobile-nav-link mobile-dropdown-link"
                                  onClick={(e) => {
                                    handleExternalLink(e, nestedItem.path);
                                    setIsMenuOpen(false);
                                  }}
                                >
                                  {nestedItem.label}
                                </a>
                              ) : (
                                <Link
                                  key={nestedItem.path}
                                  to={nestedItem.path}
                                  className="mobile-nav-link mobile-dropdown-link"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {nestedItem.label}
                                </Link>
                              )
                            ))}
                          </div>
                        </div>
                      );
                    }
                    
                    // Regular dropdown item
                    return dropdownItem.isExternal ? (
                      <a
                        key={dropdownItem.path}
                        href={dropdownItem.path}
                        className="mobile-nav-link mobile-dropdown-link"
                        onClick={(e) => {
                          handleExternalLink(e, dropdownItem.path);
                          setIsMenuOpen(false);
                        }}
                      >
                        {dropdownItem.label}
                      </a>
                    ) : (
                      <Link
                        key={dropdownItem.path}
                        to={dropdownItem.path}
                        className="mobile-nav-link mobile-dropdown-link"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {dropdownItem.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
